<template>
  <div class="auth-code">
    <div class="auth-code__text-content">
      <TypographyComp
        v-editor-inline="'Подтверждение_смс_заг'"
        class="auth-code__title"
        preset="heading-m"
      />

      <TypographyComp
        v-editor-inline="'Подтверждение_смс_текст'"
        preset="text-s"
        class="auth-code__description"
      />
    </div>

    <div class="auth-code__input-wrapper">
      <PinInput
        v-model="code"
        :has-error="hasError"
      />

      <div
        v-if="timeLeft > 0"
        class="auth-code__timer"
      >
        <TypographyComp
          preset="text-s"
          :class="resendAfterClasses"
        >
          Повторная отправка кода через
          <TypographyComp
            preset="text-s"
            class="auth-code__timer-time"
          >
            {{ formattedTimeLeft }}
          </TypographyComp>
        </TypographyComp>
      </div>

      <TypographyComp
        v-else
        v-editor-inline="'Подтверждение_смс_повтор'"
        preset="text-s"
        class="auth-code__resend"
        @click="handleResend"
      />
    </div>

    <CommonButton
      :disabled="!isValid || isLoading"
      :is-loading="isLoading"
      @click="handleSubmit"
    >
      <TypographyComp
        v-editor-inline="'Подтверждение_смс_кнопка'"
      />
    </CommonButton>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import PinInput from '@common/components/popups/auth/PinInput.vue';
import { useAuth } from '@common/composables/useAuth';
import { RESEND_TIME } from '@common/types/auth';
import type { AuthStatus } from '@common/types/auth';

const { isCodeLengthValid } = useAuth();

interface Props {
  isLoading: boolean;
  authStatus: AuthStatus;
}

const { authStatus } = defineProps<Props>();

const emit = defineEmits<{
  submit: [code: string];
  back: [];
}>();

const code = ref<string>('');
const timeLeft = ref<number>(RESEND_TIME);
let timer: ReturnType<typeof setInterval> | null = null;

const isValid = computed<boolean>(() => isCodeLengthValid(code.value));

const startTimer = (): void => {
  if (timer) {
    clearInterval(timer);
  }
  timer = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value -= 1;
    }
    else {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }
  }, 1000);
};

const formattedTimeLeft = computed<string>(() => {
  const leftSeconds = timeLeft.value.toString().padStart(2, '0');

  return `00:${leftSeconds}`;
});

const handleSubmit = (): void => {
  if (isValid.value) {
    emit('submit', code.value);
  }
};

const handleResend = (): void => {
  timeLeft.value = RESEND_TIME;
  startTimer();
};

onMounted(() => {
  startTimer();
});

onBeforeUnmount(() => {
  if (!timer) return;
  clearInterval(timer);
  timer = null;
});

const resendAfterClasses = computed<(string | Record<string, boolean>)[]>(() => [
  'auth-code__resend-after',
  {
    'auth-code__resend-after--error': hasError.value,
  },
]);

const hasError = computed<boolean>(() => authStatus === 'error');
</script>

<style lang="scss" scoped>
.auth-code {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 32rem;
  margin: 0 auto;
  gap: 2.8rem;
  position: relative;

  &__text-content {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
  }

  &__title {
    text-align: center;
  }

  &__description {
    text-align: center;
  }

  &__input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    align-items: center;
  }

  &__timer {
    font-size: 1.4rem;
    color: $color-gray;
    text-align: center;

    &-time {
      color: $color-sirena-main;
    }
  }

  &__resend {
    color: $color-sirena-main;
    cursor: pointer;
    text-decoration: underline;

    &-after {
      color: inherit;

      &--error {
        color: $color-sirena-wrong;
      }
    }
  }
}
</style>
